import { UserRole } from 'src/api/endpoints/users/users.types.new';
import { CurrentUser } from 'src/api/endpoints/auth/api';
import { Branch } from 'src/api/endpoints/branches/api';
import { Navigate, useParams } from 'react-router-dom';
import useAppSelector from 'src/hooks/useAppSelector';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { Helmet } from 'react-helmet-async';
import Loader from 'src/components/Loader';
import { FC, ReactElement } from 'react';
import { find, size } from 'lodash';
import {
    branchesIsInitialized,
    selectActiveBranchId,
    setActiveBranch,
    setAvailableBranches,
    setInitialized,
} from 'src/store/selected-branch';

export type AuthorizedUserProps = {
    user: CurrentUser;
    currentBranchId: number;
    currentBranch: Branch;
    defaultBranch: Branch;
    permissions: CurrentUser['permissions'];
    role: UserRole;
    branches: Branch[];
};

export type ProtectedRouteProps = {
    user?: CurrentUser | null;
    userBranches?: Branch[];
    expectedRoles?: UserRole[];

    // Default /login
    redirectPath?: string;
    element?: FC<AuthorizedUserProps>;
};

export const ProtectedRoute: FC<ProtectedRouteProps> = (
    props: ProtectedRouteProps
): ReactElement | null => {
    const {
        redirectPath,
        user,
        userBranches,
        expectedRoles = [],
        element,
    } = props;

    const dispatch = useAppDispatch();

    const urlBranchId: number = Number(useParams().branchId);
    const activeBranchId: number | null = useAppSelector(selectActiveBranchId);

    const isInitialized = useAppSelector(branchesIsInitialized);

    if (user === undefined || userBranches === undefined)
        return (
            <div
                style={{
                    height: '100vh',
                    width: '100vw',
                }}
            >
                <Helmet title={'Загрузка · Личный кабинет'} />
                <Loader />
            </div>
        );

    if (!user || (size(expectedRoles) && !expectedRoles.includes(user.role.id)))
        return <Navigate to={redirectPath ?? '/login'} replace={true} />;

    const branchId: number = !Number.isNaN(urlBranchId)
        ? urlBranchId
        : (activeBranchId ?? user.defaultBranchId);

    if (element) {
        if (!isInitialized) {
            dispatch(setAvailableBranches(userBranches));
            dispatch(setActiveBranch({ branchId }));
            dispatch(setInitialized());
        }

        return element({
            user,
            branches: userBranches,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            currentBranch: find(userBranches, b => b.id === branchId)!,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            defaultBranch: find(
                userBranches,
                b => b.id === user.defaultBranchId
            )!,
            currentBranchId: branchId,
            role: user.role.id,
            permissions: user.permissions,
        });
    }

    return null;
};
